import { useEffect, useState, type ComponentType } from 'react'
import type { AdminClient } from './api/client.js'
import { ApiClientError } from './api/types.js'
import type { SessionStore } from './auth/session.js'
import {
  IconAccess,
  IconBrand,
  IconChats,
  IconContacts,
  IconCredentials,
  IconInstances,
  IconLogout,
  IconPipelines,
  IconSearch,
  IconVariables,
  IconWebhooks,
} from './icons.js'
import { parseRoute, SCREEN_TITLES, type ScreenName } from './router.js'
import { Access } from './screens/Access.js'
import { Chats } from './screens/Chats.js'
import { Contacts } from './screens/Contacts.js'
import { Credentials } from './screens/Credentials.js'
import { Instances } from './screens/Instances.js'
import { Login } from './screens/Login.js'
import { Setup } from './screens/Setup.js'
import { PipelineEditor } from './screens/PipelineEditor.js'
import { Pipelines } from './screens/Pipelines.js'
import { Variables } from './screens/Variables.js'
import { Webhooks } from './screens/Webhooks.js'
import { Alert } from './ui/Alert.js'
import { CommandPalette } from './ui/CommandPalette.js'
import { ThemeToggle } from './ui/ThemeToggle.js'
import type { ThemeStore } from './ui/theme.js'
import type { WorkspaceStore } from './ui/workspace.js'
import { ToastProvider } from './ui/toasts.js'

export interface AppProps {
  client: AdminClient
  session: SessionStore
  /** Выбранное оформление портала — им владеет точка входа, не оболочка. */
  theme: ThemeStore
  /**
   * Настройки рабочего места: свёрнутая палитра редактора (FR-ADM-044).
   * Владеет ими точка входа — по той же причине, что и темой: постоянное
   * хранилище вкладки собирается в одном месте, а не берётся из глобала
   * там, где понадобилось.
   */
  workspace: WorkspaceStore
}

/*
 * Что показывать в боковой навигации.
 *
 * Переписки и контактов здесь нет намеренно: они принадлежат конкретному
 * инстансу, а не арендатору — без выбранного инстанса показывать нечего,
 * и раздел упирался в надпись «откройте со страницы инстансов». Открывают
 * их из карточки инстанса, где они и по смыслу лежат (решение владельца,
 * 2026-08-10).
 */
/*
 * Навигация сгруппирована по тому, к чему разделы относятся, а не
 * перечислена подряд.
 *
 * Ключи и переменные — не самостоятельные сущности портала: ими
 * пользуются ТОЛЬКО узлы сценариев. Ровным списком в меню они выглядели
 * как что-то отдельное, и человек справедливо спрашивал, что это за
 * раздел и почему он не внутри сценариев (замечание владельца).
 *
 * Собственный раздел они всё же сохраняют, а не прячутся вкладкой внутри
 * редактора: ключи общие для всех сценариев, живут по своим правам
 * (credentials.read/write) и заводит их часто не тот, кто собирает
 * сценарий.
 */
const NAV_GROUPS: readonly { title: string | null; screens: readonly ScreenName[] }[] = [
  { title: null, screens: ['instances'] },
  { title: 'Сценарии', screens: ['pipelines', 'credentials', 'variables'] },
  { title: null, screens: ['webhooks', 'access'] },
]

/**
 * Одна строка под заголовком раздела: что это и зачем. Заголовок называет
 * раздел, подпись объясняет — без неё «Ключи внешних служб» приходится
 * угадывать.
 */
const SCREEN_SUBTITLES: Partial<Record<ScreenName, string>> = {
  credentials:
    'Пароли и ключи, которыми узлы сценариев обращаются в чужие службы — 1С, CRM, сайт. Хранятся зашифрованными и после сохранения не показываются.',
  variables:
    'Общие значения для всех сценариев арендатора: адрес службы, название компании, номер для пересылки. Подставляются выражением {{ $vars.КЛЮЧ }}.',
  webhooks: 'Куда платформа сообщает о событиях: входящих сообщениях, статусах доставки, отказах.',
  access: 'Кто работает в портале, с какими правами и какими ключами к нему обращаются внешние системы.',
}

const SCREEN_ICONS: Record<ScreenName, ComponentType<{ className?: string }>> = {
  instances: IconInstances,
  webhooks: IconWebhooks,
  chats: IconChats,
  contacts: IconContacts,
  pipelines: IconPipelines,
  credentials: IconCredentials,
  variables: IconVariables,
  access: IconAccess,
}

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

function messageOf(cause: unknown): string {
  return cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE
}

/**
 * Точка сборки портала. Полка уведомлений живёт здесь, а не внутри
 * экранов: сообщение об исходе действия обязано появляться в одном и том же
 * месте, с какого бы экрана оно ни пришло, — а «одно и то же место» бывает
 * только у общего предка.
 */
export function App(props: AppProps) {
  return (
    <ToastProvider>
      <Portal {...props} />
    </ToastProvider>
  )
}

/**
 * Каркас приложения: пускает на экран входа, пока нет сессии, и разбирает
 * адрес вкладки в текущий раздел после входа.
 */
function Portal(props: AppProps) {
  const { client, session, theme, workspace } = props

  // Токен доступа не переживает перезагрузку вкладки (FR-ADM-003), а
  // refresh-токен — переживает: его наличие в хранилище и есть признак
  // «уже входил» до перезагрузки. Токен доступа клиент обновит сам при
  // первом же запросе (FR-ADM-004) — заново вводить пароль не нужно.
  const [authenticated, setAuthenticated] = useState(() => session.refreshToken() !== null)

  /**
   * Нужна ли первичная настройка (FR-INST-014). `null` — ещё спрашиваем:
   * показывать форму входа, пока неизвестно, есть ли кому входить, значит
   * мигать экраном на каждой загрузке новой установки.
   */
  const [setupRequired, setSetupRequired] = useState<boolean | null>(null)
  const [prefilledEmail, setPrefilledEmail] = useState<string | null>(null)

  // Кто сейчас работает в портале — сессия хранит только права (FR-ADM-003),
  // не id и не почту, а экрану «Доступ в портал» id нужен, чтобы не предлагать
  // отключить самого себя (FR-ADM-010). Хук объявлен до раннего возврата
  // ниже — он обязан вызываться на каждом рендере в одном порядке
  // независимо от того, вошли уже или нет.
  const [me, setMe] = useState<Awaited<ReturnType<AdminClient['me']>> | null>(null)
  // Отказ me() — например, refresh-токен пережил вкладку, но сессия на
  // сервере уже отозвана, — раньше оставлял человека в вечной «Загрузка…»
  // без причины и без выхода: промис отклонялся, а `.then` без `.catch`
  // отказ просто проглатывал (найдено на ревью задачи 14, round 1).
  const [meError, setMeError] = useState<string | null>(null)

  // Командная палитра (FR-ADM-023). Живёт в оболочке по той же причине, что
  // и кнопка выхода: она про портал целиком, а не про открытый экран.
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    // Ctrl+K и Cmd+K — одно и то же сочетание на разных клавиатурах, а не
    // два разных: на Mac роль Ctrl играет Cmd. Браузер вешает на Ctrl+K
    // свой поиск, поэтому событие приходится забирать себе.
    function приНажатии(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== 'k' || !(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      setPaletteOpen(true)
    }
    window.addEventListener('keydown', приНажатии)
    return () => window.removeEventListener('keydown', приНажатии)
  }, [])

  useEffect(() => {
    // Спрашиваем один раз и только у невошедшего: вошедшему настройка
    // заведомо не нужна — он в неё и вошёл.
    if (authenticated) {
      setSetupRequired(false)
      return
    }
    let cancelled = false
    client
      .setupRequired()
      .then((required) => {
        if (!cancelled) setSetupRequired(required)
      })
      .catch(() => {
        // Служба не ответила — показываем вход: он хотя бы объяснит отказ
        // словами сервера, а визард в такой ситуации предложил бы завести
        // учётную запись там, где она, возможно, уже есть.
        if (!cancelled) setSetupRequired(false)
      })
    return () => {
      cancelled = true
    }
  }, [authenticated, client])

  useEffect(() => {
    if (!authenticated) return
    setMeError(null)
    let cancelled = false
    client
      .me()
      .then((info) => {
        if (!cancelled) setMe(info)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setMeError(messageOf(cause))
      })
    return () => {
      cancelled = true
    }
  }, [authenticated, client])

  // finally, а не только успешный путь: клиент чистит собственную сессию
  // изнутри logout() независимо от того, ответил ли сервер (client.ts),
  // поэтому и здесь выход из состояния входа не должен ждать успеха сети —
  // висящий запрос не обязан держать человека на портале (FR-ADM-013).
  async function handleLogout() {
    try {
      await client.logout()
    } finally {
      setAuthenticated(false)
      setMe(null)
      setMeError(null)
    }
  }

  if (!authenticated) {
    if (setupRequired === null) return <p className="загрузка-оболочки">Открываем портал…</p>

    if (setupRequired) {
      return (
        <Setup
          client={client}
          onDone={(email) => {
            setPrefilledEmail(email)
            setSetupRequired(false)
          }}
        />
      )
    }

    return (
      <Login
        client={client}
        onDone={() => setAuthenticated(true)}
        {...(prefilledEmail === null ? {} : { email: prefilledEmail })}
      />
    )
  }

  const route = parseRoute(window.location.pathname)

  return (
    <div className="app-shell">
      <nav className="app-sidebar" aria-label="Разделы админки">
        <div className="app-brand">
          <IconBrand />
          <span className="app-brand__text">RK WhatsApp</span>
        </div>
        <ul className="app-nav">
          {NAV_GROUPS.map((group, index) => (
            <li key={group.title ?? `группа-${index}`} className="app-nav__группа">
              {group.title !== null && (
                <span className="app-nav__заголовок label">{group.title}</span>
              )}
              <ul>
                {group.screens.map((screen) => {
                  const ScreenIcon = SCREEN_ICONS[screen]
                  return (
                    <li key={screen}>
                      <a
                        href={`/app/${screen}`}
                        aria-current={route.screen === screen ? 'page' : undefined}
                      >
                        <ScreenIcon />
                        <span className="label">{SCREEN_TITLES[screen]}</span>
                      </a>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
        <div className="app-sidebar__footer">
          {/* Подсказка о палитре — строкой в подвале навигации, а не
              баннером поверх работы: попасться на глаза осматривающемуся
              она обязана, мешать работающему — нет. Она же и кнопка: тот,
              кто увидел сочетание, но не запомнил, открывает мышью. */}
          <button
            type="button"
            className="палитра-команд__подсказка"
            onClick={() => setPaletteOpen(true)}
          >
            <IconSearch />
            <span>Быстрый переход</span>
            <kbd>Ctrl+K</kbd>
          </button>

          <ThemeToggle store={theme} />

          {/* Кто сейчас работает в портале — здесь, а не в шапке рабочей
              области: это свойство сессии, а не страницы, на которой
              человек стоит. Пусто, пока me() не разрешился или отказал:
              заголовок раздела не обязан ждать почту. */}
          {me !== null && <div className="app-who">{me.email}</div>}
          {/* Живёт в оболочке, а не в конкретном экране: доступна и тогда,
              когда данные самого экрана не загрузились (см. meError ниже) —
              это и есть выход из зависшего состояния, а не отдельный
              механизм (FR-ADM-013). */}
          <button type="button" className="btn btn--ghost app-logout" onClick={() => void handleLogout()}>
            <IconLogout />
            <span className="label">Выйти</span>
          </button>
        </div>
      </nav>
      <div className="app-content">
        <header className="app-header">
          <div>
            <h1>{SCREEN_TITLES[route.screen]}</h1>
            {SCREEN_SUBTITLES[route.screen] !== undefined && (
              <p className="app-header__подпись">{SCREEN_SUBTITLES[route.screen]}</p>
            )}
          </div>
        </header>
        <main className="app-main">
          {route.screen === 'instances' ? (
            <Instances client={client} permissions={session.permissions()} />
          ) : route.screen === 'webhooks' ? (
            // С идентификатором — подписки конкретного инстанса: тот же
            // экран, суженный до его событий (FR-WA-025).
            <Webhooks
              client={client}
              permissions={session.permissions()}
              {...(route.id === undefined ? {} : { instanceId: route.id })}
            />
          ) : route.screen === 'chats' ? (
            route.id !== undefined ? (
              <Chats client={client} instanceId={route.id} />
            ) : (
              // Переписка привязана к инстансу — выбор конкретного инстанса
              // (переход по адресу /app/chats/:id) появится вместе с экраном
              // инстансов следующей задачей; здесь честно показываем, чего не
              // хватает, а не переписку пустого или произвольного инстанса.
              <p>Откройте переписку конкретного номера со страницы «Номера».</p>
            )
          ) : route.screen === 'pipelines' ? (
            // Адрес с идентификатором — редактор одного сценария: попасть в
            // него иначе как из списка нельзя, идентификатор человеку
            // неизвестен.
            route.id !== undefined ? (
              <PipelineEditor
                client={client}
                pipelineId={route.id}
                permissions={session.permissions()}
                workspace={workspace}
              />
            ) : (
              <Pipelines client={client} permissions={session.permissions()} />
            )
          ) : route.screen === 'credentials' ? (
            <Credentials client={client} permissions={session.permissions()} />
          ) : route.screen === 'variables' ? (
            <Variables client={client} permissions={session.permissions()} />
          ) : route.screen === 'contacts' ? (
            route.id !== undefined ? (
              <Contacts client={client} instanceId={route.id} permissions={session.permissions()} />
            ) : (
              <p>Откройте контакты конкретного номера со страницы «Номера».</p>
            )
          ) : me !== null ? (
            <Access client={client} me={me} permissions={session.permissions()} />
          ) : meError !== null ? (
            <Alert>{meError}</Alert>
          ) : (
            <p>Загрузка данных о текущем пользователе…</p>
          )}
        </main>
      </div>

      {paletteOpen && (
        <CommandPalette
          client={client}
          onClose={() => setPaletteOpen(false)}
          // Переход — обычной сменой адреса, а не своей историей: разделы
          // портала и так живут отдельными адресами, и ссылка в навигации
          // работает ровно так же.
          navigate={(path) => window.location.assign(path)}
        />
      )}
    </div>
  )
}
